import { Injectable, Input } from '@angular/core';
import { TimeInterval } from '../models/order.model';
import { Time, WorkDay } from '../models/restaurant.model';
import { RestaurantStore } from '../stores/restaurant.store';
import { FormatDataService } from './format-data.service';
import * as moment from 'moment-timezone';

@Injectable({
  providedIn: 'root'
})
export class TimeService {
  public initialDate: Date;
  public initialTimeInterval: TimeInterval;
  public hoursOfOperation: WorkDay[];
  public interval: number;
  public deliveryInterval: number;
  public pickupDaysInAdvance: any;
  public estimatedTimeInMinutes: number;
  public hideETA: boolean;
  public disableASAP: boolean;
  public selectASAP: boolean;
  public acceptingOrders: boolean;
  public timezone: string;
  public disableTodayOrders: boolean;
  public showWindowEndTime: boolean;
  public asap: boolean;

  public fullDayPickUpAvailable: boolean;
  private isDelivery: boolean;

  private get todayDayWork(): WorkDay {
    return this.getWorkDay(new Date());
  }
  constructor(
    private restaurantStore: RestaurantStore,
    private formatDataService: FormatDataService
  ) { }

  public isToday(date: Date) {
    if (!date) {
      return;
    }
    const today = new Date();
    return today.toDateString() === date.toDateString();
  }

  public generateIntervals(date?: Date, throttlingTime: number = null): TimeInterval[] {
    let initialValue = this.getStartDate(this.initialDate);
    if (date) {
      initialValue = date;
    }
    const intervals: TimeInterval[] = [];
    const workDay = this.getWorkDay(initialValue);
    const isToday = this.isToday(initialValue);
    if (!workDay || !workDay?.startTime) {
      return intervals;
    }
    workDay.startTime.forEach((days, index) => {
      let openTime = workDay.startTime[index].getDate(initialValue);
      const closeTime = workDay.endTime[index].getDate(initialValue);
      if (isToday && throttlingTime) {
        openTime = new Date(openTime.getTime() + throttlingTime);
      }
      const restaurantCloseTime = workDay.endTime[workDay.endTime.length - 1].getDate(initialValue);
      this.fullDayPickUpAvailable = this.isFullDayToPickUp(workDay, index);
      if (isToday && restaurantCloseTime.getTime() <= initialValue.getTime()) {
        return;
      }
      let startTimeDelivery = this.getStartTimeDelivery(openTime, initialValue, isToday, index);
      let endTimeDelivery = this.addMinutes(startTimeDelivery, this.interval);
      let isDeliveryStartFounded = false;
      if (isToday && closeTime < new Date()) {
        return;
      }
      while (endTimeDelivery.getTime() <= closeTime.getTime()) {
        if (this.isDelivery) {
          const deliveryInterval = this.addMinutes(this.getStartTimeDelivery(openTime, initialValue, isToday, index), this.deliveryInterval);
          const deliveryIntervalTime = deliveryInterval.getTime();
          if ((startTimeDelivery.getTime() <= deliveryIntervalTime && deliveryIntervalTime <= endTimeDelivery.getTime())) {
            isDeliveryStartFounded = true;
          } else if (isDeliveryStartFounded) {
            intervals.push({start: startTimeDelivery, end: endTimeDelivery});
          }
          startTimeDelivery = endTimeDelivery;
          endTimeDelivery = this.addMinutes(endTimeDelivery, this.interval);
        }  else {
          intervals.push({start: startTimeDelivery, end: endTimeDelivery});
          startTimeDelivery = endTimeDelivery;
          endTimeDelivery = this.addMinutes(endTimeDelivery, this.interval);
        }
      }
    });
    const newIntervals = intervals.filter(int => int.start.getTime() > new Date().getTime());
    return isToday ? newIntervals : intervals;
  }

  public generateIntervalsForCityFlavor(date: Date, estimateTime: number): TimeInterval[] {
    const currentDate = new Date(new Date().getTime() + estimateTime);
    const timeArray = [
      { hours: 11, minutes: 0 },
      { hours: 11, minutes: 45 },
      { hours: 12, minutes: 30 },
      { hours: 13, minutes: 0 },
    ];
    const intervals = timeArray
      .map(time => {
        const currentTime = new Date(date);
        currentTime.setHours(time.hours, time.minutes);
        return new TimeInterval(currentTime, currentTime);
      })
      .filter(interval => interval.start >= currentDate);

    return intervals;
  }

  public generateIntervalsForCityFlavorSpecial(date: Date, estimateTime: number): TimeInterval[] {
    const currentDate = new Date(new Date().getTime() + estimateTime);
    const timeArray = [
      { hours: 23, minutes: 0 }
    ];

    const intervals = timeArray
      .map(time => {
        const currentTime = new Date(date);
        currentTime.setHours(time.hours, time.minutes);
        return new TimeInterval(currentTime, currentTime);
      })
      .filter(interval => interval.start >= currentDate);

    return intervals;
  }



  public getWorkDay(date: Date): WorkDay {
    if (this.restaurantStore.timeRangeParams != null) {
      return this.hoursOfOperation.find( (day) => this.formatDataService.isSameDate(date, day.date));
    }
    const today = new Date();
    for (const [index] of this.hoursOfOperation.entries()) {
      if (this.formatDataService.isSameDate(date, today)) {
        return this.hoursOfOperation[index];
      }
      today.setDate(today.getDate() + 1);
    }
  }
  public isAsap(day: WorkDay): boolean {
    const now = new Date();
    if (!this.disableASAP && this.acceptingOrders) {
      for (const [index] of day.startTime.entries()) {
        const endTime = day.endTime[index].time.hour === 12 ? 0 : day.endTime[index].time.hour;
        const endTimeMeridian = day.endTime[index].meridian ? 0 : 12;
        const startTime = day.startTime[index].time.hour === 12 ? 0 : day.startTime[index].time.hour;
        const startTimeMeridian = day.startTime[index].meridian ? 0 : 12;
        const timeNow = new Time(now.getHours(), now.getMinutes());
        if ((startTime + startTimeMeridian < timeNow.hour && endTime + endTimeMeridian > timeNow.hour) ||
          (startTime + startTimeMeridian === timeNow.hour && day.startTime[index].time.minute < timeNow.minute) ||
          (endTime + endTimeMeridian  === timeNow.hour && day.endTime[index].time.minute > timeNow.minute)) {
          return true;
        }
      }
    }
    return false;
  }
  public getStartDate(initialDate?: Date): Date {
    let startDate = initialDate || new Date();
    const isToday = this.isToday(startDate);
    const todayDayWork = this.todayDayWork;
    if (isToday && (this.disableTodayOrders || todayDayWork?.endTime[todayDayWork?.endTime.length - 1].today.getTime() <= startDate?.getTime() || !this.todayDayWork?.enable || this.isDeliveryAfterWorkDayEnd(startDate))) {
      startDate = this.getNearestWorkDate(this.addDays(startDate, 1));
    } else if (isToday) {
      startDate.setMinutes(startDate.getMinutes() + this.estimatedTimeInMinutes);
    }
    return startDate;
  }
  public getNearestWorkDate(startDate: Date): Date {
    let workDay = this.getWorkDay(startDate);
    let date = startDate;
    while (workDay && !workDay.enable) {
      date = this.addDays(date, 1);
      workDay = this.getWorkDay(date);
      if (!workDay) {
        break;
      }
    }
    return date;
  }
  public hasAsap(date: Date): boolean {
    if (this.isDelivery && this.isToday(date) && this.acceptingOrders) {
      return true;
    } else if (this.disableASAP || !this.isToday(date)) {
      return false;
    }
    const workDay = this.getWorkDay(date);
    return this.isAsap(workDay);
  }

  public nextWorkDaysInAdvance(startDate:Date,nextDays:number) {
    const days = [];
    const daysInAdvance = nextDays > 0 ? nextDays : 1;
    const finalDayInAdvance = new Date();
    finalDayInAdvance.setDate(finalDayInAdvance.getDate() + daysInAdvance);
    let day = this.getStartDate(startDate);
    if (this.formatDataService.isSameDate(day, finalDayInAdvance) || this.formatDataService.isAfterDate(day, finalDayInAdvance)) {
      return days;
    }
    days.push(day);
    while (true) {
      day = this.getNearestWorkDate(this.addDays(day, 1));
      if (!this.isToday(day)) {
        day.setMinutes(0);
        day.setHours(0);
      }
      if (this.formatDataService.isSameDate(day, finalDayInAdvance) || this.formatDataService.isAfterDate(day, finalDayInAdvance) || !this.isHoursOfOperation(day) ) {
        break;
      }
      days.push(day);
    }
    return days;
  }

  private isHoursOfOperation(day: Date): boolean {
    const targetDateString = day.toISOString().split('T')[0];

    // Check if any WorkDay in the array has the same date
    return this.hoursOfOperation.some(workDay => {
        const workDayDateString = workDay.date.toISOString().split('T')[0];
        return targetDateString === workDayDateString && workDay.enable;
    });
}

  public initDate(data: Date[]): Date {
    if (!this.initialDate) {
      return data[0];
    }
    return data.find(date => date.toDateString() === this.initialDate.toDateString()) || data[0];
  }

  private isFullDayToPickUp(day: WorkDay, index: number) {
    let dayStartMeridian = day.startTime[index].meridian ? 0 : 12;
    const dayStartTime = day.startTime[index].time.hour;
    let dayEndMeridian = day.endTime[index].meridian ? 0 : 12;
    const dayEndTime = day.endTime[index].time.hour;

    if (dayStartTime === 12 && dayStartMeridian === 0) {
      dayStartMeridian = -12;
    } else if (dayStartTime === 12 && dayStartMeridian === 12) {
      dayStartMeridian = 0;
    }
    if (dayEndTime === 12 && dayEndMeridian === 0) {
      dayEndMeridian = -12;
    } else if (dayEndTime === 12 && dayEndMeridian === 12) {
      dayEndMeridian = 0;
    }
    const workHours = ((dayEndTime + dayEndMeridian) - (dayStartTime + dayStartMeridian)) * 60 + (day.startTime[index].time.minute + day.endTime[index].time.minute);

    return this.interval > workHours;
  }

  private addMinutes(date: Date, minutes: number): Date {
    return new Date(date.getTime() + minutes * 60000);
  }

  private addDays(date, days): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private getStartTimeDelivery(startTime: Date, initialDate: Date, isToday = false, index = null) {
    if (!isToday || (isToday && index !== 0)) {
      return this.roundDate(startTime, true);
    }
    const startDeliveryTime = initialDate.getTime() >= startTime.getTime() ? initialDate : startTime;
    const includeBorders = !this.hasAsap(initialDate);
    return this.roundDate(startDeliveryTime, includeBorders);
  }
  private roundDate(date: Date, includeBorder = false): Date {
    const minutes = this.interval - (date.getMinutes() % this.interval);
    if (includeBorder) {
      const isRoundedTime = minutes === this.interval;
      return isRoundedTime ? date : this.addMinutes(date, minutes);
    }
    return this.addMinutes(date, minutes);
  }
  private isDeliveryAfterWorkDayEnd(initialValue: Date) {
    if (this.isDelivery && this.isToday(initialValue)) {
      const deliveryInterval = this.addMinutes(initialValue, this.deliveryInterval);
      const deliveryIntervalTime = deliveryInterval.getTime();
      const endWorkDay = this.todayDayWork.endTime[this.todayDayWork.endTime.length - 1].today.getTime();
      if (deliveryIntervalTime > endWorkDay) {
        return true;
      }
    }
    return false;
  }

  public generateTimeIntervalsCF(shifts: any[], interval:number, isDelivery=false, selectedDeliveryLocation = null): TimeInterval[] {
        const intervals: TimeInterval[] = [];
        const uniqueIntervals = new Set<string>();
        shifts?.forEach(shift => {
          const day = shift.day;
          let startTime = shift.start_time;
          let endTime = shift.end_time;
          let startDateTime = moment.tz(`${day} ${startTime}`, 'M-D-YYYY h:mm A', this.timezone);
          let endDateTime = moment.tz(`${day} ${endTime}`, 'M-D-YYYY h:mm A', this.timezone);
          let deliveryLocation = shift.location_id.delivery_locations.find(d => d.name == selectedDeliveryLocation)
          if(isDelivery && shift.location_id.delivery_enabled
            && deliveryLocation) {
              for (const deliveryHour of deliveryLocation.delivery_hours) {
                let startTime = deliveryHour.delivery_start_time;
                let endTime = deliveryHour.delivery_end_time;

                let startTimeDelivery = moment.tz(`${day} ${startTime}`, 'M-D-YYYY h:mm A', this.timezone);
                let endTimeDelivery = moment.tz(`${day} ${endTime}`, 'M-D-YYYY h:mm A', this.timezone);

                while (startTimeDelivery.isBefore(endTimeDelivery)) {
                    const nextInterval = moment(startTimeDelivery).add(interval, 'minutes');
                    const intervalEnd = nextInterval.isAfter(endTimeDelivery) ? endTimeDelivery : nextInterval;
                    const intervalKey = `${startTimeDelivery.toISOString()}-${intervalEnd.toISOString()}`;

                    if (!uniqueIntervals.has(intervalKey)) {
                        uniqueIntervals.add(intervalKey);
                        intervals.push(new TimeInterval(startTimeDelivery.toDate(), intervalEnd.toDate()));
                    }
                    startTimeDelivery = nextInterval;
                }
            }

          } else if(!isDelivery) {
            while (startDateTime.isBefore(endDateTime)) {
              const nextInterval = moment(startDateTime).add(interval, 'minutes');
              const intervalEnd = nextInterval.isAfter(endDateTime) ? endDateTime : nextInterval;
              const intervalKey = `${startDateTime.toISOString()}-${intervalEnd.toISOString()}`;
              if (!uniqueIntervals.has(intervalKey)) {
                uniqueIntervals.add(intervalKey);
                intervals.push(new TimeInterval(startDateTime.toDate(), intervalEnd.toDate()));
              }
              startDateTime = nextInterval;
            }
          }


        });

        return intervals;
      }

}
